/**
 * Generates a poster URL for video files
 * @param url - The video URL
 * @returns The poster URL or undefined if not a video
 */
export const getVideoPosterUrl = (url: string): string | undefined => {
  if (!url) return undefined;

  const [baseUrl, query] = url.split('?');

  // Check if it's a video file
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.ogg', '.m4v'];
  const isVideo = videoExtensions.some((ext) => baseUrl.toLowerCase().includes(ext));

  if (!isVideo) return undefined;

  // Generate poster URL by replacing video extension with .jpg
  const posterBaseUrl = baseUrl.replace(/\.(mp4|mov|avi|webm|ogg|m4v)$/i, '.jpg');
  return query ? `${posterBaseUrl}?${query}` : posterBaseUrl;
};

export const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const [baseUrl] = url.split('?');
  return /\.(mp4|mov|avi|webm|ogg|m4v)$/i.test(baseUrl);
};

/**
 * Generates a preview thumbnail URL for Content Hub videos.
 * For Cloudinary video URLs we add a `so_0` transformation to reliably fetch a frame as jpg.
 */
export const getContentHubVideoPreviewUrl = (url: string): string | undefined => {
  if (!url) return undefined;

  const [baseUrl] = url.split('?');
  const isCloudinaryVideo =
    baseUrl.includes('res.cloudinary.com') && baseUrl.includes('/video/upload/');

  if (!isCloudinaryVideo) return getVideoPosterUrl(url);

  // Cloudinary: /video/upload/... -> /video/upload/so_0/...
  const withFrame = baseUrl.replace('/video/upload/', '/video/upload/so_0/');
  return withFrame.replace(/\.(mp4|mov|avi|webm|ogg|m4v)$/i, '.jpg');
};

/**
 * Shortens a filename to a specified length
 * @param fileName - The original filename
 * @param maxLength - Maximum length (default: 20)
 * @returns Shortened filename
 */
export const shortenFileName = (fileName: string, maxLength = 20): string => {
  if (!fileName) return '';

  if (fileName.length <= maxLength) return fileName;

  const extension = fileName.split('.').pop();
  const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));

  if (!extension) {
    return `${fileName.substring(0, maxLength - 3)}...`;
  }

  const availableLength = maxLength - extension.length - 4; // 4 for '...' and '.'
  return `${nameWithoutExt.substring(0, availableLength)}....${extension}`;
};

/**
 * Allowed video types for file uploads
 */
export const allowedVideoTypes = [
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/webm',
  'video/ogg'
];

const CLOUDINARY_ROOT_URL = 'https://res.cloudinary.com/beatly';

/**
 * Generates a reliable thumbnail URL for media items
 * For images, returns the original URL
 * For videos, generates a Cloudinary thumbnail URL using cloudinary_id
 * @param media - The media object containing url, resource_type, and optionally cloudinary_id
 * @returns The thumbnail URL
 */
export const getMediaThumbnailUrl = (media: DraftMedia): string => {
  if (media.resource_type === 'image') {
    return media.url;
  }

  // For videos, use Cloudinary thumbnail if cloudinary_id is available
  if (media.cloudinary_id) {
    return `${CLOUDINARY_ROOT_URL}/video/upload/w_400,c_limit,q_auto/${media.cloudinary_id}.jpg`;
  }

  // Fallback to URL-based replacement
  return media.url.replace('.mp4', '.jpg');
};

/**
 * Generates a reliable poster URL for video preview using Cloudinary
 * @param media - The media object containing url and optionally cloudinary_id
 * @returns The poster URL for video preview
 */
export const getVideoPosterFromMedia = (media: DraftMedia): string | undefined => {
  if (!media.url) return undefined;

  // Use Cloudinary thumbnail if cloudinary_id is available
  if (media.cloudinary_id) {
    return `${CLOUDINARY_ROOT_URL}/video/upload/w_800,c_limit,q_auto/${media.cloudinary_id}.jpg`;
  }

  // Fallback to URL-based replacement
  return getVideoPosterUrl(media.url);
};
